import React from 'react';
import { Heart, Sparkles, ChevronRight, Smartphone, Linkedin, MessageCircle, Download, Share2, Play, Trophy, Handshake, Music, Flame } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { AFTERMOVIE_ID } from './ThankYouPopup';
import { PRE_REGISTRO_2027_URL } from '../config/constants';
import { trackPreregistroClick } from '../hooks/usePreregistroCount';

const RAW = 'https://customer-assets-lxgj4vgw.emergentagent.net/job_d4b3d2e1-67da-40cf-8efa-61dbe3f79433/artifacts/';
const W = `https://images.weserv.nl/?url=customer-assets-lxgj4vgw.emergentagent.net/job_d4b3d2e1-67da-40cf-8efa-61dbe3f79433/artifacts/`;

const p = (file, w) => ({ share: `${RAW}${file}`, src: `${W}${file}&w=${w}&q=78&output=webp` });

const PHOTOS = [
  { ...p('1n0hbru2_WhatsApp%20Image%202026-08-28%20at%206.16.17%20PM.jpeg', 1000), big: true },
  { ...p('fkpqv7qy_WhatsApp%20Image%202026-08-28%20at%202.17.35%20PM.jpeg', 600) },
  { ...p('zq8hokyq_WhatsApp%20Image%202026-08-28%20at%202.16.54%20PM.jpeg', 600) },
  { ...p('xmog7czu_WhatsApp%20Image%202026-08-28%20at%206.16.12%20PM.jpeg', 600) },
  { ...p('r9h6ij85_WhatsApp%20Image%202026-08-28%20at%206.16.15%20PM.jpeg', 1000), wide: true },
  { ...p('kcrd7vym_WhatsApp%20Image%202026-08-29%20at%205.57.30%20AM.jpeg', 1200), wide: true },
  { ...p('y1vgtj9s_WhatsApp%20Image%202026-08-28%20at%206.16.11%20PM.jpeg', 600) },
  { ...p('ep48jh29_WhatsApp%20Image%202026-09-01%20at%2012.04.57%20PM.jpeg', 600) },
  { ...p('unadp0rc_WhatsApp%20Image%202026-09-01%20at%206.33.25%20PM.jpeg', 1200), wide: true },
  { ...p('tfmerjhr_WhatsApp%20Image%202026-08-28%20at%2011.42.10%20PM.jpeg', 1200), wide: true },
  { ...p('vjbfovm9_WhatsApp%20Image%202026-08-28%20at%2011.42.09%20PM%20%281%29.jpeg', 1200), wide: true },
  { ...p('bhnpsk40_WhatsApp%20Image%202026-08-28%20at%2011.42.10%20PM%20%281%29.jpeg', 1200), wide: true },
  { ...p('1mx3no3m_WhatsApp%20Image%202026-08-28%20at%2011.42.09%20PM.jpeg', 1200), wide: true },
  { ...p('qtttpqmm_WhatsApp%20Image%202026-08-28%20at%2011.42.09%20PM%20%282%29.jpeg', 1200), wide: true },
];

export const WHATSAPP_2027_URL = `https://wa.me/525579311747?text=${encodeURIComponent('¡Hola! Quiero ser de los primeros en recibir información de la 21ª Convención Nacional ASOFOM 2027.')}`;

const shareText = (isES) =>
  isES
    ? 'Así se vivió la 20ª Convención Nacional ASOFOM en Cancún. ¡Nos vemos en 2027! #20AñosASOFOM'
    : 'This is how the 20th ASOFOM National Convention was lived in Cancún. See you in 2027! #20AñosASOFOM';

const ShareBar = ({ photo, isES }) => {
  const text = shareText(isES);
  const links = [
    { icon: Linkedin, label: 'LinkedIn', href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(photo.share)}` },
    { icon: MessageCircle, label: 'WhatsApp', href: `https://wa.me/?text=${encodeURIComponent(`${text} ${photo.share}`)}` },
    { icon: Share2, label: 'X', href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(photo.share)}` },
    { icon: Download, label: isES ? 'Descargar' : 'Download', href: photo.share, download: true },
  ];
  return (
    <div className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-2 pb-3 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-300">
      {links.map((l) => (
        <a
          key={l.label}
          href={l.href}
          target="_blank"
          rel="noopener noreferrer"
          title={l.label}
          aria-label={l.label}
          data-testid={`gallery-share-${l.label.toLowerCase()}`}
          className="w-9 h-9 rounded-full bg-black/60 backdrop-blur-md border border-white/30 flex items-center justify-center text-white hover:bg-[#FF6B35] hover:border-[#FF6B35] transition-colors"
          onClick={(e) => e.stopPropagation()}
        >
          <l.icon className="w-4 h-4" />
        </a>
      ))}
    </div>
  );
};

const Aftermovie = ({ isES }) => (
  <div className="relative max-w-5xl mx-auto mb-14" data-testid="post-event-aftermovie">
    <div className="absolute -inset-3 sm:-inset-5 bg-gradient-to-r from-[#FF6B35]/30 via-[#D4AF37]/20 to-[#FF6B35]/30 rounded-[2rem] blur-2xl opacity-70" aria-hidden="true"></div>
    <div className="relative flex justify-center mb-4">
      <div className="inline-flex items-center gap-2 bg-[#FF6B35]/15 border border-[#FF6B35]/40 rounded-full px-4 py-1.5">
        <Play className="w-3.5 h-3.5 text-[#FF6B35] fill-[#FF6B35]" />
        <span className="text-xs font-bold text-[#FF6B35] uppercase tracking-widest">{isES ? 'Aftermovie oficial · Edición 20' : 'Official aftermovie · Edition 20'}</span>
      </div>
    </div>
    <div className="relative rounded-3xl overflow-hidden border border-[#D4AF37]/40 shadow-[0_30px_80px_rgba(0,0,0,0.6)] bg-black">
      <div className="aspect-video">
        <iframe
          src={`https://www.youtube-nocookie.com/embed/${AFTERMOVIE_ID}?rel=0&modestbranding=1&playsinline=1`}
          title={isES ? 'Aftermovie oficial · 20ª Convención Nacional ASOFOM' : 'Official aftermovie · 20th ASOFOM National Convention'}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          loading="lazy"
          className="w-full h-full"
        />
      </div>
    </div>
    <p className="mt-4 text-center text-sm text-white/55">
      {isES ? '3 minutos que resumen 20 años de legado. Súbele el volumen.' : '3 minutes that sum up 20 years of legacy. Turn up the volume.'}
    </p>
  </div>
);

const HIGHLIGHTS = (isES) => [
  { icon: Handshake, t: isES ? 'Speed Dating de Fondeo' : 'Funding Speed Dating', d: isES ? 'Cientos de reuniones 1 a 1 entre SOFOMes y fondeadores institucionales.' : 'Hundreds of 1-on-1 meetings between SOFOMs and institutional funders.' },
  { icon: Trophy, t: isES ? 'Ranking ASOFOM 2026' : 'ASOFOM Ranking 2026', d: isES ? 'Reconocimos a las instituciones que están elevando el estándar del sector.' : 'We recognized the institutions raising the bar for the sector.' },
  { icon: Music, t: isES ? 'The Grand Finale' : 'The Grand Finale', d: isES ? 'Una noche Black & White y un concierto que nadie olvidará.' : 'A Black & White night and a concert nobody will forget.' },
  { icon: Flame, t: isES ? 'Sold Out histórico' : 'Historic Sold Out', d: isES ? 'Tickets y patrocinios agotados semanas antes. Un récord en 20 ediciones.' : 'Tickets and sponsorships sold out weeks in advance. A record in 20 editions.' },
];

const PostEventSection = () => {
  const { language } = useLanguage();
  const isES = language === 'es';
  return (
    <section id="gracias" className="relative py-20 lg:py-24 bg-[#071528] overflow-hidden scroll-mt-20" data-testid="post-event-section">
      <div className="absolute -top-32 -right-32 w-[480px] h-[480px] bg-[#FF6B35]/15 rounded-full blur-[140px]" aria-hidden="true"></div>
      <div className="absolute -bottom-32 -left-32 w-[480px] h-[480px] bg-[#D4AF37]/10 rounded-full blur-[140px]" aria-hidden="true"></div>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="inline-flex items-center gap-2 bg-[#D4AF37]/15 border border-[#D4AF37]/40 rounded-full px-4 py-1.5 mb-5">
            <Heart className="w-4 h-4 text-[#D4AF37]" />
            <span className="text-sm font-bold text-[#D4AF37] uppercase tracking-widest">{isES ? 'Aftermovie · Highlights · Galería' : 'Aftermovie · Highlights · Gallery'}</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight">
            {isES ? 'Así se vivió la edición 20' : 'This is how edition 20 was lived'}
          </h2>
          <p className="mt-5 text-white/70 text-base md:text-lg leading-relaxed">
            {isES
              ? 'ASOFOM es mucho más que una asociación: es una comunidad que corre junta al amanecer, cierra negocios al mediodía y celebra su legado por la noche. Tres días en Cancún que nos recordaron por qué el futuro del financiamiento se construye juntos.'
              : 'ASOFOM is much more than an association: it is a community that runs together at sunrise, closes deals at noon and celebrates its legacy at night. Three days in Cancún that reminded us why the future of financing is built together.'}
          </p>
        </div>

        <Aftermovie isES={isES} />

        <div className="grid grid-cols-3 gap-4 max-w-2xl mx-auto mb-14" data-testid="post-event-stats">
          {[
            { n: '+800', l: isES ? 'asistentes' : 'attendees' },
            { n: '+60', l: 'speakers' },
            { n: '20', l: isES ? 'años de legado' : 'years of legacy' },
          ].map((s) => (
            <div key={s.l} className="bg-white/[0.05] border border-white/10 rounded-2xl px-4 py-4 text-center">
              <div className="text-3xl font-black text-[#FF6B35]">{s.n}</div>
              <div className="text-xs text-white/60 font-semibold uppercase tracking-wide mt-1">{s.l}</div>
            </div>
          ))}
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-14" data-testid="post-event-highlights">
          {HIGHLIGHTS(isES).map((h) => (
            <div key={h.t} className="group bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-[#FF6B35]/40 rounded-2xl p-5 transition-colors">
              <div className="w-11 h-11 rounded-xl bg-[#FF6B35]/15 border border-[#FF6B35]/30 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <h.icon className="w-5 h-5 text-[#FF6B35]" />
              </div>
              <h3 className="text-white font-bold text-base leading-tight">{h.t}</h3>
              <p className="text-white/55 text-sm mt-2 leading-relaxed">{h.d}</p>
            </div>
          ))}
        </div>

        <div className="text-center mb-6">
          <h3 className="text-2xl sm:text-3xl font-bold text-white">{isES ? 'Galería oficial' : 'Official gallery'}</h3>
          <p className="mt-2 text-sm text-white/50 inline-flex items-center gap-2">
            <Share2 className="w-4 h-4 text-[#FF6B35]" />
            {isES ? 'Pasa el cursor sobre cada foto y compártela en tus redes con un clic' : 'Hover over each photo and share it on your networks with one click'}
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-14" data-testid="post-event-gallery">
          {PHOTOS.map((ph, i) => (
            <div key={i} className={`relative overflow-hidden rounded-2xl border border-white/10 group ${ph.big ? 'col-span-2 row-span-2' : ''} ${ph.wide ? 'col-span-2' : ''}`}>
              <img
                src={ph.src}
                alt={isES ? `Momento ${i + 1} · 20ª Convención Nacional ASOFOM` : `Moment ${i + 1} · 20th ASOFOM National Convention`}
                className={`w-full h-full object-cover ${ph.wide ? 'aspect-[8/3]' : 'aspect-[4/3]'} group-hover:scale-105 transition-transform duration-700`}
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#071528]/70 via-transparent to-transparent"></div>
              <ShareBar photo={ph} isES={isES} />
            </div>
          ))}
        </div>

        <div id="2027" className="relative overflow-hidden rounded-[2rem] bg-gradient-to-r from-[#0B1E3D] via-[#16324F] to-[#0B1E3D] border border-[#D4AF37]/30 px-6 sm:px-10 py-10 text-center scroll-mt-24" data-testid="post-event-fomo">
          <div aria-hidden="true" className="absolute -right-6 -bottom-16 select-none pointer-events-none leading-none font-black text-white/[0.05] text-[11rem]">21</div>
          <div className="inline-flex items-center gap-2 bg-[#FF6B35]/15 border border-[#FF6B35]/40 rounded-full px-4 py-1.5 mb-4">
            <Sparkles className="w-4 h-4 text-[#FF6B35]" />
            <span className="text-xs font-bold text-[#FF6B35] uppercase tracking-widest">{isES ? '21ª Convención Nacional · 2027' : '21st National Convention · 2027'}</span>
          </div>
          <h3 className="text-2xl sm:text-4xl font-bold text-white leading-tight">
            {isES ? 'Lo que pasó en Cancún no se repite. Lo que viene, se supera.' : "What happened in Cancún won't repeat. What's coming will top it."}
          </h3>
          <p className="mt-4 text-white/70 max-w-2xl mx-auto leading-relaxed">
            {isES
              ? 'La edición 20 fue Sold Out semanas antes. Los negocios, las amistades y los momentos que aquí nacieron ya son historia… y la 21ª Convención Nacional ya está en camino. Sé de los primeros en enterarte de sede, fechas y preventa: no dejes que te la cuenten dos veces.'
              : 'Edition 20 sold out weeks in advance. The deals, friendships and moments born here are already history… and the 21st National Convention is on its way. Be among the first to hear about venue, dates and presale: do not let them tell you about it twice.'}
          </p>
          <div className="mt-7 flex flex-wrap justify-center gap-4">
            <a href={PRE_REGISTRO_2027_URL} target="_blank" rel="noopener noreferrer" onClick={trackPreregistroClick} className="reservar-cta inline-flex items-center gap-2 bg-[#FF6B35] hover:bg-[#E55A28] text-white font-bold px-7 py-3.5 rounded-full shadow-[0_15px_40px_rgba(255,107,53,0.4)] transition-all hover:-translate-y-0.5" data-testid="post-event-2027-cta">
              <Flame className="w-5 h-5" />
              {isES ? 'Pre-regístrate para 2027' : 'Pre-register for 2027'}
            </a>
            <a href="/app-asofom" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/30 text-white font-semibold px-7 py-3.5 rounded-full transition-all" data-testid="post-event-app-cta">
              <Smartphone className="w-5 h-5" />
              {isES ? 'Revive la experiencia en la App' : 'Relive the experience in the App'}
            </a>
            <a href="/perspectivas" className="inline-flex items-center gap-2 text-white/80 hover:text-white font-semibold px-4 py-3.5 transition-colors" data-testid="post-event-perspectivas-cta">
              {isES ? 'Lee Perspectivas' : 'Read Perspectivas'}
              <ChevronRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PostEventSection;
